import { Injectable } from '@angular/core';
import { AngularFirestore, AngularFirestoreCollection } from '@angular/fire/compat/firestore';
import { Observable, map } from 'rxjs';

import { classToPlain, plainToClass } from 'class-transformer';
import { CorpModel } from 'src/app/models';
import { ClientEnvironmentModel } from 'src/app/models/client-environment';
import { COLLECTION_NAMES } from './constants';

/** `environments-restored`: the corp already existed and only its missing environments document was written. */
export type CorpCreationResult = 'created' | 'environments-restored';

@Injectable({
  providedIn: 'root',
})
export class CorpsService {
  // Yes Public! the CorpHierarchiesService will be using this
  corpsCollection: AngularFirestoreCollection<CorpModel>;
  private environmentsCollection: AngularFirestoreCollection<ClientEnvironmentModel>;

  constructor(private afs: AngularFirestore) {
    this.corpsCollection = afs.collection<CorpModel>(COLLECTION_NAMES.CORPS);
    this.environmentsCollection = afs.collection<ClientEnvironmentModel>(COLLECTION_NAMES.ENVIRONMENTS);
  }

  /**
   * Writes the corp document and its `environments` document in a single atomic batch, so a
   * corp can never end up without environments (which hides every assistant on the dashboard).
   *
   * Re-running for a corp that already exists but has no environments document only restores
   * the environments, leaving the existing corp document untouched.
   */
  async addCorpWithEnvironment(corp: CorpModel, environment: ClientEnvironmentModel): Promise<CorpCreationResult> {
    const corpId = corp.id; // avoiding error prone scenarios
    const corpRef = this.corpsCollection.doc(corpId).ref;
    const environmentRef = this.environmentsCollection.doc(corpId).ref;
    const [corpSnapshot, environmentSnapshot] = await Promise.all([corpRef.get(), environmentRef.get()]);

    if (corpSnapshot.exists && environmentSnapshot.exists) {
      throw new Error(`Corporation "${corpId}" already exists`);
    }

    const batch = this.afs.firestore.batch();
    if (!corpSnapshot.exists) {
      batch.set(corpRef, Object.assign({}, corp));
    }
    if (!environmentSnapshot.exists) {
      batch.set(environmentRef, Object.assign({}, classToPlain(environment) as ClientEnvironmentModel));
    }
    await batch.commit();

    return corpSnapshot.exists ? 'environments-restored' : 'created';
  }

  updateCorp(corp: CorpModel): Promise<void> {
    return this.corpsCollection.doc(corp.id).update(Object.assign({}, corp));
  }

  getCorpById(id: string): Observable<CorpModel | null> {
    return this.corpsCollection
      .doc(id)
      .valueChanges()
      .pipe(
        map(corp => {
          return plainToClass(CorpModel, corp);
        }),
      );
  }

  getCorps(): Observable<CorpModel[]> {
    return this.corpsCollection.valueChanges().pipe(
      map(corps => {
        return corps.map(corp => plainToClass(CorpModel, corp));
      }),
    );
  }

  removeCorp(id: string) {
    return this.corpsCollection.doc(id).delete();
  }
}
